/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import EnvironmentService from 'cio/services/environment';
import { OtherExportType } from 'cio/services/customer';

interface PeopleExportOtherModalArgs {
  onExportButtonClick: (
    exportType: OtherExportType,
    attributesToExport: string[],
    duplicatedAttribute: string | null,
  ) => unknown;
  onClose: () => unknown;
}

// When a number of attributes is 6 or less, they are all visible
// in a dropdown list and there is no need for search functionality.
const MIN_ATTRIBUTES_NUMBER_TO_DISPLAY_DD_SEARCH = 7;

const ATTRIBUTES_ALWAYS_INCLUDED_IN_DUPLICATES_EXPORT = ['cio_id', 'id'];

const ATTRIBUTES_ALWAYS_INCLUDED_IN_DEVICES_EXPORT = [
  'cio_id',
  'id',
  'email',
  'device_token',
  'platform',
  'last_status',
  'last_used',
  'last_used_RFC3339',
];

export default class PeopleExportOtherModal extends Component<PeopleExportOtherModalArgs> {
  @service environment!: EnvironmentService;

  @tracked selectedExportType!: OtherExportType;
  @tracked duplicatedAttributeSelected!: string;
  @tracked exportAllAttributes = true;
  @tracked selectedAttributesToExport: string[] = [];

  constructor(owner: unknown, args: PeopleExportOtherModalArgs) {
    super(owner, args);

    this.selectedExportType = this.envHasAtLeastOneAttribute
      ? OtherExportType.DuplicatedAttribute
      : OtherExportType.AllDevices;
    this.duplicatedAttributeSelected = this.envHasEmailAttribute
      ? 'email'
      : this.duplicatedAttributeOptions[0]?.value ?? '';
  }

  @computed('selectedExportType')
  get isExportingDevices() {
    return this.selectedExportType === OtherExportType.AllDevices;
  }

  @computed('isExportingDevices', 'duplicatedAttributeSelected')
  get alwaysIncludedAttributes(): string[] {
    return this.isExportingDevices
      ? ATTRIBUTES_ALWAYS_INCLUDED_IN_DEVICES_EXPORT
      : [...ATTRIBUTES_ALWAYS_INCLUDED_IN_DUPLICATES_EXPORT, this.duplicatedAttributeSelected].uniq();
  }

  @computed('alwaysIncludedAttributes', 'environment.sentAttributes')
  get availableAttributes(): string[] {
    return this.environment.sentAttributes.filter((value) => !this.alwaysIncludedAttributes.includes(value));
  }

  @computed('environment.sentAttributes')
  get duplicatedAttributeOptions(): Record<string, any>[] {
    return this.environment.sentAttributes
      .filter((value) => value !== 'cio_id')
      .map((value) => ({ value, label: value }));
  }

  @computed('duplicatedAttributeOptions')
  get displayDuplicatedAttributeOptionsSearch(): boolean {
    return this.duplicatedAttributeOptions.length >= MIN_ATTRIBUTES_NUMBER_TO_DISPLAY_DD_SEARCH;
  }

  @computed('environment.sentAttributes')
  get envHasEmailAttribute(): boolean {
    return this.environment.sentAttributes.includes('email');
  }

  @computed('duplicatedAttributeOptions')
  get envHasAtLeastOneAttribute(): boolean {
    return this.duplicatedAttributeOptions.length > 0;
  }

  @computed('availableAttributes')
  get attributesToExportOptions(): Record<string, any>[] {
    return this.availableAttributes.map((value) => ({ value, label: value }));
  }

  @computed('attributesToExportOptions')
  get displayAttributeToExportOptionsSearch(): boolean {
    return this.attributesToExportOptions.length >= MIN_ATTRIBUTES_NUMBER_TO_DISPLAY_DD_SEARCH;
  }

  @computed('alwaysIncludedAttributes')
  get alwaysIncludedAttributesString(): string {
    return this.alwaysIncludedAttributes.join(', ');
  }

  @action
  onExportDuplicatedAttributeOptionSelected() {
    this.selectedExportType = OtherExportType.DuplicatedAttribute;
  }

  @action
  onExportAllDevicesOptionSelected() {
    this.selectedExportType = OtherExportType.AllDevices;
  }

  @action
  onSelectedDuplicatedAttributeChange(selectedOption: Record<string, any>) {
    this.duplicatedAttributeSelected = selectedOption.value;
  }

  @action
  onSelectedAttributesToExportChange(selectedOptions: Record<string, any>[]) {
    this.selectedAttributesToExport = selectedOptions.map((option) => option.value);
  }

  @action
  onExportButtonClick() {
    let attributesToExport = this.exportAllAttributes ? this.availableAttributes : this.selectedAttributesToExport;
    let duplicatedAttribute = this.isExportingDevices ? null : this.duplicatedAttributeSelected;

    if (duplicatedAttribute != null) {
      attributesToExport.push(duplicatedAttribute);
    }

    this.args?.onExportButtonClick(this.selectedExportType, attributesToExport, duplicatedAttribute);
  }
}
